import type { LoaiThueTncn } from '@qlk/shared';
import {
  LOAI_THUE_TNCN_LABEL,
  dinhDangVnd,
  nhanKyDoiSoat,
  sangVnd,
  thucChuyenVnd,
  tinhThueTncn,
} from '@qlk/shared';
import { useState } from 'react';

import { HopThoai, Nut, O, dinhDangTien, homNayVn } from '../../../ui/index.js';
import { useCaiDatCongTy, useDanhDauThanhToan } from '../api.js';

export interface HopThanhToanProps {
  period: string;
  userId: string;
  tenNguoi: string;
  phaiTra: number;
  /** Diện thuế đã chụp lúc chốt; `null` khi người đó chưa khai hồ sơ. */
  loaiThue: LoaiThueTncn | null;
  /** Tỷ giá đã nhập trước đó (xem trước sau khi chốt), nếu có. */
  tyGiaDaCo: number | null;
  onDong: () => void;
}

/**
 * Đánh dấu đã chuyển tiền.
 *
 * Cảnh báo trước khi bấm: sau bước này **không mở lại được**. Số tiền VND, tỷ
 * giá và thuế TNCN là việc của bước 3 — ảnh chụp đã chừa ô, giao diện chưa hỏi.
 */
export function HopThanhToan({
  period,
  userId,
  tenNguoi,
  phaiTra,
  loaiThue,
  tyGiaDaCo,
  onDong,
}: HopThanhToanProps) {
  const danhDau = useDanhDauThanhToan();
  const caiDat = useCaiDatCongTy();

  const [ngay, setNgay] = useState(homNayVn());
  const [ghiChu, setGhiChu] = useState('');
  const [tep, setTep] = useState('');
  const [tyGia, setTyGia] = useState(tyGiaDaCo === null ? '' : String(tyGiaDaCo));
  const [thueTay, setThueTay] = useState('');
  const [lyDoThue, setLyDoThue] = useState('');

  const soTyGia = Number(tyGia.replace(/[.\s]/g, '').replace(',', '.'));
  const coTyGia = Number.isFinite(soTyGia) && soTyGia > 0;
  const dien = loaiThue ?? 'khau_tru_10';

  /*
   * Tính thử ngay trong hộp bằng **đúng hàm** server dùng (`tinhThueTncn` ở
   * shared), nên số xem trước không bao giờ khác số lưu xuống.
   */
  const thu =
    coTyGia && caiDat.data
      ? tinhThueTncn({
          phaiTraVnd: sangVnd(phaiTra, soTyGia),
          loaiThue: dien,
          pctMacDinh: caiDat.data.thueTncnPctMacDinh,
          nguongVnd: caiDat.data.nguongChiMoiLanVnd,
        })
      : null;

  const soThueTay = Number(thueTay.replace(/[.\s]/g, '').replace(',', '.'));
  const coThueTay = thueTay.trim() !== '' && Number.isFinite(soThueTay) && soThueTay >= 0;
  const thuePhaiNhapTay = thu !== null && !thu.tuTinh;
  const thueCuoi = coThueTay ? soThueTay : (thu?.thue ?? 0);

  /* Ghi đè số app tính thì phải có lý do; diện luỹ tiến thì không cần. */
  const thieuLyDo = coThueTay && thu !== null && thu.tuTinh && lyDoThue.trim().length < 3;
  const hopLe = ngay !== '' && !thieuLyDo && !(thuePhaiNhapTay && !coThueTay);

  return (
    <HopThoai
      title={`Đánh dấu đã thanh toán — ${tenNguoi}`}
      rong="md"
      onDong={onDong}
      chanHop={
        <>
          {danhDau.isError && (
            <p role="alert" className="mr-auto self-center text-phu text-bad-chu">
              {danhDau.error.message}
            </p>
          )}
          <Nut kieu="phu" onClick={onDong}>
            Huỷ
          </Nut>
          <Nut
            kieu="chinh"
            disabled={!hopLe}
            dangChay={danhDau.isPending}
            onClick={() => {
              danhDau.mutate(
                {
                  period,
                  userId,
                  ngay,
                  ...(ghiChu.trim() ? { ghiChu: ghiChu.trim() } : {}),
                  ...(tep.trim() ? { tepChungTu: tep.trim() } : {}),
                  ...(coTyGia ? { tyGiaVnd: soTyGia } : {}),
                  ...(coThueTay ? { thueTncn: soThueTay } : {}),
                  ...(coThueTay && lyDoThue.trim() ? { thueGhiDeLyDo: lyDoThue.trim() } : {}),
                },
                { onSuccess: onDong },
              );
            }}
          >
            Đã thanh toán
          </Nut>
        </>
      }
    >
      <div className="space-y-4">
        <p className="text-co-ban text-chu">
          {nhanKyDoiSoat(period)} của <strong>{tenNguoi}</strong>:{' '}
          <strong className="tabular-nums">{dinhDangTien(phaiTra)}</strong>.
        </p>

        <p className="rounded-[10px] bg-warn-nen px-3 py-2 text-phu text-warn-chu">
          Sau khi đánh dấu thanh toán, kỳ này <strong>không mở lại được</strong>. Sai sót xử lý bằng
          một khoản điều chỉnh ở kỳ sau, ghi lý do dẫn chiếu kỳ này.
        </p>

        <O label="Ngày chuyển tiền" type="date" value={ngay} onChange={setNgay} required />

        <O
          label="Tỷ giá USD → VND"
          type="number"
          min={0}
          step={1}
          value={tyGia}
          onChange={setTyGia}
          moTa="Số VND do app tính, không nhập tay. Tỷ giá đóng băng theo phiên bản này."
        />

        {thu && (
          <div className="rounded-[10px] bg-info-nen px-3 py-2 text-phu text-info-chu">
            <p>
              Phải trả <strong>{dinhDangVnd(sangVnd(phaiTra, soTyGia))}</strong> · diện{' '}
              {LOAI_THUE_TNCN_LABEL[dien]}
            </p>
            <p className="mt-0.5">{thu.ghiChu}</p>
            <p className="mt-0.5">
              Thuế <strong>{dinhDangVnd(thueCuoi)}</strong> · thực chuyển{' '}
              <strong>{dinhDangVnd(thucChuyenVnd(sangVnd(phaiTra, soTyGia), thueCuoi))}</strong>
            </p>
          </div>
        )}

        {thuePhaiNhapTay && (
          <p className="rounded-[10px] bg-warn-nen px-3 py-2 text-phu text-warn-chu">
            Diện <strong>{LOAI_THUE_TNCN_LABEL[dien]}</strong> — app không tự tính. Nhập số thuế kế
            toán đã tính.
          </p>
        )}

        <O
          label="Thuế TNCN (VND) — để trống nếu dùng số app tính"
          type="number"
          min={0}
          step={1000}
          value={thueTay}
          onChange={setThueTay}
        />

        {coThueTay && thu?.tuTinh === true && (
          <O
            label="Lý do khác số app tính"
            value={lyDoThue}
            onChange={setLyDoThue}
            nhieuDong
            required
            moTa="Lý do đi vào nhật ký thao tác."
          />
        )}

        <O
          label="Tệp chứng từ"
          value={tep}
          onChange={setTep}
          moTa="Tên hoặc đường dẫn tệp đã lưu"
        />
        <O label="Ghi chú" value={ghiChu} onChange={setGhiChu} nhieuDong />

        {!coTyGia && (
          <p className="text-phu text-chu-mo">
            Chưa nhập tỷ giá thì vẫn đánh dấu thanh toán được, nhưng biên bản để trống phần VND và
            thuế — và sau khi đã thanh toán thì <strong>không sửa được nữa</strong>.
          </p>
        )}
      </div>
    </HopThoai>
  );
}
